// Loading checklist ↔ PDI sheets: the rules both sides share. A boat is matched by SAP, an item by its
// name (a loading item vs the PDI row's SAP_Model text, folded so case, accents and punctuation don't
// matter), plus any extra PDI wordings an admin saved as aliases. Status words map both ways:
// PDI Yes ↔ loaded, PDI No ↔ N/A (the boat doesn't have the item), PDI blank ↔ pending. A PDI Hold
// keeps the Loading item pending and is never blanked by a Loading change. When the two sides disagree
// the latest status change wins; these helpers make that call so the worker and the tests agree on it.
import { LOADING_ITEMS } from './loading.js';
/** app_config key holding the admin's aliases: loading item name → extra PDI names that count as it. */
export const LOADING_PDI_ALIASES_KEY = 'loading.pdiAliases';
/** "Set all" and "Send to PDI" leave PDI Hold rows alone; only a single, explicit item change overrides
 *  a hold. Flip to false to let bulk changes clear holds too. */
export const BULK_KEEPS_PDI_HOLD = true;
/** at most this many aliases per loading item. */
export const LOADING_PDI_MAX_ALIASES = 20;
// ── names ──────────────────────────────────────────────────────────────────────
/** fold a name for matching: accents and Greek tonos dropped, lower case, final sigma as σ, and every
 *  run of anything that isn't a letter or digit turned into one space. */
export function foldForMatch(s) {
    return String(s ?? '')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/ς/g, 'σ')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
}
/** the loading item a name refers to, by folded name (null when it isn't one of the 28). */
function itemIndexByName(name) {
    const f = foldForMatch(name);
    return f ? LOADING_ITEMS.findIndex((n) => foldForMatch(n) === f) : -1;
}
/** tidy an alias map read from a request or from config: keep only known item names (stored under the
 *  item's exact spelling), trim, drop blanks and duplicates (by folded form), drop an alias that is just
 *  the item's own name, and cap each list. `conflicts` lists aliases that would point at two different
 *  items (another item's name, or the same alias on two items); the save route refuses those. */
export function cleanAliases(raw) {
    const aliases = {};
    const conflicts = [];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { aliases, conflicts };
    const nameFolds = LOADING_ITEMS.map((n) => foldForMatch(n));
    // folded alias → the item that claimed it first
    const claimed = new Map();
    const lists = [];
    for (const [key, value] of Object.entries(raw)) {
        const item = itemIndexByName(key);
        if (item < 0 || !Array.isArray(value))
            continue;
        const seen = new Set();
        const list = [];
        for (const v of value) {
            if (typeof v !== 'string')
                continue;
            const alias = v.trim().replace(/\s+/g, ' ');
            const f = foldForMatch(alias);
            if (!f || seen.has(f) || f === nameFolds[item])
                continue;
            seen.add(f);
            list.push(alias);
        }
        if (list.length)
            lists.push({ item, list: list.slice(0, LOADING_PDI_MAX_ALIASES) });
    }
    for (const { item, list } of lists) {
        const name = LOADING_ITEMS[item];
        const keep = [];
        for (const alias of list) {
            const f = foldForMatch(alias);
            const other = nameFolds.indexOf(f);
            if (other >= 0 && other !== item) {
                conflicts.push(`"${alias}" is the name of ${LOADING_ITEMS[other]}`);
                continue;
            }
            const prev = claimed.get(f);
            if (prev === item)
                continue; // the same alias listed twice under one item
            if (prev !== undefined) {
                conflicts.push(`"${alias}" is on both ${LOADING_ITEMS[prev]} and ${name}`);
                continue;
            }
            claimed.set(f, item);
            keep.push(alias);
        }
        if (keep.length)
            aliases[name] = [...(aliases[name] ?? []), ...keep];
    }
    return { aliases, conflicts };
}
/** a matcher from a PDI row's text to the loading item number it stands for. Item names first, then
 *  aliases; when two would claim the same folded text, the first one registered keeps it. */
export function buildItemMatcher(aliases) {
    const keys = new Map();
    LOADING_ITEMS.forEach((n, i) => {
        const f = foldForMatch(n);
        if (f && !keys.has(f))
            keys.set(f, i);
    });
    for (const [name, list] of Object.entries(aliases ?? {})) {
        const i = itemIndexByName(name);
        if (i < 0 || !Array.isArray(list))
            continue;
        for (const a of list) {
            const f = foldForMatch(a);
            if (f && !keys.has(f))
                keys.set(f, i);
        }
    }
    // the same few hundred PDI wordings repeat on every boat, so remember each raw name's answer.
    const memo = new Map();
    return (pdiName) => {
        if (memo.has(pdiName))
            return memo.get(pdiName);
        const hit = keys.get(foldForMatch(pdiName));
        if (memo.size < 5000)
            memo.set(pdiName, hit);
        return hit;
    };
}
/** match one boat's PDI item rows (in sheet order) to loading items. The first row that matches an item
 *  is the one used; any later row for the same item only marks it ambiguous. */
export function matchBoatItems(rows, match) {
    const out = new Map();
    for (const row of rows) {
        const i = match(row.name);
        if (i === undefined)
            continue;
        const m = out.get(i);
        if (!m)
            out.set(i, { row, ambiguous: false, otherRowIds: [] });
        else {
            m.ambiguous = true;
            m.otherRowIds.push(row.rowId);
        }
    }
    return out;
}
// ── status words ───────────────────────────────────────────────────────────────
/** a PDI Status cell as one of Yes / No / Hold / blank. "N/A" counts as No. Anything else is null and
 *  is never synced (shown as is). */
export function normalizePdiStatus(raw) {
    if (raw === null || raw === undefined)
        return '';
    const s = String(raw).trim().toLowerCase();
    if (s === '')
        return '';
    if (s === 'yes')
        return 'Yes';
    if (s === 'no' || s === 'n/a' || s === 'na')
        return 'No';
    if (s === 'hold')
        return 'Hold';
    return null;
}
/** PDI → Loading: Yes → loaded, No → N/A, Hold and blank → pending. */
export function pdiToLoading(p) {
    return p === 'Yes' ? 'loaded' : p === 'No' ? 'na' : 'pending';
}
/** Loading → PDI: loaded → Yes, N/A → No, pending → blank. */
export function loadingToPdi(s) {
    return s === 'loaded' ? 'Yes' : s === 'na' ? 'No' : '';
}
/** what to write to PDI after a Loading item changed to `next`. `current` is the PDI Status read just
 *  before writing (null when it holds a word the sync doesn't know). A change back to pending never
 *  blanks a Hold; bulk changes leave every Hold alone unless `includeHolds`. */
export function planWriteThrough(next, current, opts = {}) {
    const desired = loadingToPdi(next);
    if (current === 'Hold' && next === 'pending')
        return { write: false, result: 'kept-pdi-hold' };
    if (current === 'Hold' && opts.bulk && BULK_KEEPS_PDI_HOLD && !opts.includeHolds) {
        return { write: false, result: 'kept-pdi-hold' };
    }
    if (current === desired)
        return { write: false, result: 'synced' };
    return { write: true, value: desired };
}
/** decide one matched item.
 *  - PDI only counts as changed when its Status differs from the one the sync last saw on that same PDI
 *    row (a row touched for a comment doesn't count). Then the newer of the PDI row's modifiedAt and
 *    Loading's status time wins, a tie going to Loading. A missing time counts as older on the Loading
 *    side and as newer on the PDI side, so a change PDI made is never lost for want of a timestamp.
 *  - The first time the sync sees a PDI row for an item (a row from before the sync, a boat's new block,
 *    a re-pointed sheet, a same-named row added above), nothing says which side changed last: a row's
 *    modifiedAt also moves for comments. A Loading tick (Loaded / N/A) is then kept and PDI only noted,
 *    so a new or blank PDI row never wipes it. A pending item has no tick to lose, so PDI's Yes / No
 *    fills it, unless a person set it back to pending after the PDI row last changed. */
export function decidePair(i) {
    const P = i.pdi;
    const L = i.loading;
    const target = pdiToLoading(P.status);
    const agreed = P.status === 'Hold' ? 'pdi-hold' : 'in-sync';
    if (!L) {
        return target === 'pending'
            ? { state: agreed, action: 'none' }
            : { state: 'pdi-newer', action: 'apply-pdi', status: target };
    }
    // what the sync last saw counts only if it saw it on this very row
    const known = L.seen !== null && L.seenRow === P.rowId;
    if (L.status === target)
        return { state: agreed, action: known && L.seen === P.status ? 'none' : 'record-seen' };
    // they differ
    const apply = { state: 'pdi-newer', action: 'apply-pdi', status: target };
    const keep = { state: 'not-in-pdi', action: 'record-seen' };
    const pAt = timeMs(P.at);
    const lAt = timeMs(L.statusAt);
    if (!known) {
        if (L.status !== 'pending')
            return keep;
        return lAt !== null && pAt !== null && lAt >= pAt ? keep : apply;
    }
    if (L.seen === P.status)
        return { state: 'not-in-pdi', action: 'none' }; // PDI hasn't moved; Loading changed after
    if (pAt === null || lAt === null || pAt > lAt)
        return apply;
    return keep;
}
/** when a Loading item's status was last set. Rows from before the sync have no status_at, so a
 *  loaded / N/A one falls back to updated_at; a legacy pending row was never set by a person → null. */
export function effectiveStatusAt(r) {
    if (r.statusAt)
        return r.statusAt;
    return r.status !== 'pending' && r.updatedAt ? r.updatedAt : null;
}
/** milliseconds (whole seconds) for a D1 time ('YYYY-MM-DD HH:MM:SS', UTC) or an ISO time with Z or an
 *  offset. null when blank or unreadable. */
export function timeMs(s) {
    if (!s)
        return null;
    const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?\s*(Z|[+-]\d{2}:?\d{2})?$/i.exec(String(s).trim());
    if (!m)
        return null;
    let zone = m[3] ? m[3].toUpperCase() : 'Z';
    if (zone !== 'Z' && !zone.includes(':'))
        zone = `${zone.slice(0, 3)}:${zone.slice(3)}`;
    const ms = Date.parse(`${m[1]}T${m[2]}${zone}`);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) * 1000 : null;
}
/** any time timeMs reads → D1's 'YYYY-MM-DD HH:MM:SS' (UTC). */
export function toD1Time(iso) {
    const ms = timeMs(iso);
    return ms === null ? null : new Date(ms).toISOString().slice(0, 19).replace('T', ' ');
}
/** the order to look for a boat's PDI block in: the current season first, then earlier seasons newest
 *  first, then later ones oldest first. The current season stays even when it isn't in the list. */
export function pdiSeasonOrder(current, seasonIds) {
    const start = (id) => {
        const n = parseInt(id, 10);
        return Number.isNaN(n) ? 0 : n;
    };
    const cur = start(current);
    const others = [...new Set(seasonIds)].filter((id) => id !== current);
    const earlier = others.filter((id) => start(id) < cur).sort((a, b) => start(b) - start(a));
    const later = others.filter((id) => start(id) >= cur).sort((a, b) => start(a) - start(b));
    return [current, ...earlier, ...later];
}
//# sourceMappingURL=loadingPdi.js.map